/**
 * Permission Utility Functions
 * Handles all permission checking logic for RBAC system
 */

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user's permissions
 * @param {String} requiredPermission - Required permission (e.g., "menu.view")
 * @returns {Boolean}
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }

  // Check for exact match
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }

  // Check for wildcard match (e.g., "menu.*" grants all menu permissions)
  const [module] = requiredPermission.split('.');
  if (userPermissions.includes(`${module}.*`)) {
    return true;
  }

  // Super admin wildcard (grants everything)
  if (userPermissions.includes('*.*') || userPermissions.includes('admin.*')) {
    return true;
  }

  return false;
};

/**
 * Check if user has ANY of the required permissions
 * @param {Array} userPermissions - Array of user's permissions
 * @param {Array} requiredPermissions - Array of required permissions
 * @returns {Boolean}
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No permissions required
  }

  return requiredPermissions.some(permission => 
    hasPermission(userPermissions, permission)
  );
};

/**
 * Check if user has ALL required permissions
 * @param {Array} userPermissions - Array of user's permissions
 * @param {Array} requiredPermissions - Array of required permissions
 * @returns {Boolean}
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No permissions required
  }

  return requiredPermissions.every(permission => 
    hasPermission(userPermissions, permission)
  );
};

/**
 * Get user permissions from localStorage or user object
 * @param {Object} user - User object from auth context
 * @returns {Array}
 */
export const getUserPermissions = (user) => {
  // If user object has permissions directly
  if (user?.permissions && Array.isArray(user.permissions)) {
    return user.permissions;
  }

  // If user is admin (not staff), grant all permissions
  if (user?.role === 'admin' || user?.role === 'restaurant_admin') {
    return ['*.*']; // Grant all permissions
  }

  // Try to get from staff_token in localStorage
  try {
    const staffToken = localStorage.getItem('staff_token');
    if (staffToken) {
      const session = JSON.parse(staffToken);
      if (session.permissions && Array.isArray(session.permissions)) {
        return session.permissions;
      }
    }
  } catch (error) {
    console.error('Error getting permissions from localStorage:', error);
  }

  return [];
};

/**
 * Check if user is admin (has full access)
 * @param {Object} user - User object
 * @returns {Boolean}
 */
export const isAdmin = (user) => {
  if (!user) return false;
  
  // Check if it's a staff login - staff never gets admin privileges
  if (user.isStaffLogin === true || user.role === 'staff') {
    return false;
  }

  // Check role
  if (user.role === 'admin' || user.role === 'restaurant_admin') {
    return true;
  }

  // If not staff login and user exists, treat as admin (regular login)
  return true;
};

/**
 * Get filtered navigation items based on permissions
 * @param {Array} navItems - Array of navigation items
 * @param {Array} userPermissions - User's permissions
 * @returns {Array}
 */
export const getFilteredNavItems = (navItems, userPermissions) => {
  if (!navItems || !Array.isArray(navItems)) {
    return [];
  }

  return navItems.filter(item => {
    // If no permissions required, show the item
    if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
      return true;
    }

    // Check if user has any of the required permissions
    return hasAnyPermission(userPermissions, item.requiredPermissions);
  });
};

/**
 * Permission to route mapping
 * Define which permissions are required for each route
 */
export const ROUTE_PERMISSIONS = {
  '/dashboard': ['reports.view', 'orders.view'],
  '/menu-management': ['menu.view', 'menu.edit'],
  '/table-management': ['tables.view', 'tables.edit'],
  '/categories': ['categories.view', 'menu.view'],
  '/orders': ['orders.view', 'orders.edit'],
  '/discounts': ['menu.edit', 'settings.edit'],
  '/upsale-discounts': ['menu.edit', 'settings.edit'],
  '/printer-settings': ['settings.view', 'settings.edit'],
  '/voice-ai-settings': ['bot.view', 'bot.config'],
  '/roles': ['staff.manage', 'settings.edit'],
  '/staff': ['staff.manage', 'staff.view'],
  '/audit-logs': ['actionlog.view', 'staff.manage'],
  '/reports': ['reports.view', 'reports.export'],
  '/pos': ['orders.view', 'orders.create', 'menu.view'],
};

/**
 * Check if user can access a route
 * @param {String} path - Route path
 * @param {Array} userPermissions - User's permissions
 * @returns {Boolean}
 */
export const canAccessRoute = (path, userPermissions) => {
  // If no permissions defined for route, allow access
  if (!ROUTE_PERMISSIONS[path]) {
    return true;
  }

  const requiredPermissions = ROUTE_PERMISSIONS[path];
  return hasAnyPermission(userPermissions, requiredPermissions);
};
